/**
 * Usuários e permissões (só o dono): quem acessa a empresa, em quais grupos está (pode estar em vários; as permissões
 * somam) e, na aba Grupos, o que cada grupo pode ver ou alterar em cada área. Dono e Super admin acessam tudo; só o
 * dono mexe aqui. Aprovar receita: só quem tem CRF (marca de farmacêutico do usuário).
 */
import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Crown, Mail, Plus, RotateCcw, Save, Trash2, Users as UsersIcon } from 'lucide-react';
import { api, dateTime } from '../api.ts';
import { useMe } from '../components/AuthGate.tsx';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { Modal } from './CatalogSettings.tsx';
import { PERMISSIONS, type Level, type PermKey, type Perms } from '@berytus/contracts';

interface UserRow { id: string; name: string; email: string; owner: boolean; groups: string[]; active: boolean; me: boolean; lastLoginAt: string | null; pending: boolean; addedAt: string; pharmacist: boolean; crf: string | null }
interface Group { key: string; name: string; system: boolean; full: boolean; editable: boolean; customized: boolean; perms: Perms; users: number }

export function UsersPage() {
  const me = useMe();
  const isOwner = me.tenant.role === 'owner' && !me.impersonating;
  const [tab, setTab] = useState<'users' | 'groups'>('users');
  const groups = useQuery({ queryKey: ['access-groups'], queryFn: () => api<Group[]>('/account/groups') });
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Usuários e permissões</h1>
          <p>Quem acessa a {me.tenant.name} e o que cada grupo pode ver ou alterar. Uma pessoa pode estar em vários grupos: as permissões somam. Dono e Super admin acessam tudo.</p>
        </div>
      </div>
      <div className="seg" role="group" aria-label="Ver" style={{ marginBottom: 12 }}>
        <button type="button" aria-pressed={tab === 'users'} onClick={() => setTab('users')}>Usuários</button>
        <button type="button" aria-pressed={tab === 'groups'} onClick={() => setTab('groups')}>Grupos e permissões</button>
      </div>
      {groups.error ? <ErrorBox error={groups.error} /> : !groups.data ? <Loading /> : tab === 'users'
        ? <UsersTab groups={groups.data} canManage={isOwner} />
        : <GroupsTab groups={groups.data} canManage={isOwner} />}
    </>
  );
}

function GroupChips({ keys, groups }: { keys: string[]; groups: Group[] }) {
  if (!keys.length) return <span className="muted small">nenhum grupo</span>;
  return <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>{keys.map((k) => <Tag key={k} tone={k === 'super_admin' ? 'info' : 'off'}>{groups.find((g) => g.key === k)?.name ?? k}</Tag>)}</span>;
}

function GroupPicker({ value, onChange, groups }: { value: string[]; onChange: (v: string[]) => void; groups: Group[] }) {
  return (
    <div style={{ display: 'grid', gap: 6, gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
      {groups.map((g) => (
        <label key={g.key} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" className="check" checked={value.includes(g.key)}
            onChange={(e) => onChange(e.target.checked ? [...value, g.key] : value.filter((k) => k !== g.key))} />
          <span>{g.name}{g.full ? <span className="small muted"> (tudo)</span> : null}</span>
        </label>
      ))}
    </div>
  );
}

function UsersTab({ groups, canManage }: { groups: Group[]; canManage: boolean }) {
  const me = useMe();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['team'], queryFn: () => api<UserRow[]>('/account/users') });
  const [inviting, setInviting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['team'] }); void qc.invalidateQueries({ queryKey: ['access-groups'] }); };
  const setPharma = useMutation({
    mutationFn: (v: { id: string; pharmacist: boolean; crf?: string | null }) => api(`/account/users/${v.id}`, { method: 'PUT', json: { pharmacist: v.pharmacist, ...(v.crf !== undefined ? { crf: v.crf } : {}) } }),
    onSuccess: refresh,
  });
  const [pharmaFor, setPharmaFor] = useState<UserRow | null>(null);
  const remove = useMutation({ mutationFn: (id: string) => api(`/account/users/${id}`, { method: 'DELETE' }), onSuccess: refresh });
  const resend = useMutation({
    mutationFn: (u: UserRow) => api<{ mailSent: boolean; link?: string }>(`/account/users/${u.id}/resend`, { method: 'POST', json: {} }).then((r) => ({ ...r, u })),
    onSuccess: (r) => setNotice(r.mailSent ? `Convite reenviado para ${r.u.email}.` : `O e-mail não saiu. Mande este link para ${r.u.name}: ${r.link}`),
  });
  const err = remove.error ?? resend.error;

  return (
    <>
      {canManage && <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}><button className="btn primary" type="button" onClick={() => setInviting(true)}><Plus size={16} /> Convidar usuário</button></div>}
      {notice && <div className="notice small" style={{ marginBottom: 12, overflowWrap: 'anywhere' }}>{notice}</div>}
      {err && <ErrorBox error={err} />}
      <div className="panel flush">
        {q.isPending ? <Loading /> : q.error ? <div style={{ padding: 18 }}><ErrorBox error={q.error} /></div> : (
          <div style={{ overflowX: 'auto' }}>
            <table className="simple">
              <thead><tr><th>Nome</th><th>Grupos</th><th title="Aprova ou recusa receitas de medicamentos (Mercado Livre)">Farmacêutico (CRF)</th><th>Último acesso</th><th /></tr></thead>
              <tbody>
                {q.data.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <b>{u.name}</b>{u.me && <span className="muted"> (você)</span>}
                      <div className="small muted">{u.email}</div>
                    </td>
                    <td>
                      {u.owner ? <Tag tone="ok"><Crown size={11} /> Dono · tudo</Tag> : <GroupChips keys={u.groups} groups={groups} />}
                      {canManage && <button className="btn sm quiet" type="button" style={{ marginLeft: 4 }} onClick={() => setEditing(u)}>Alterar</button>}
                    </td>
                    <td className="small">
                      {u.pharmacist ? <Tag tone="ok">CRF {u.crf ?? '—'}</Tag> : <span className="muted">não</span>}
                      {canManage && <button className="btn sm quiet" type="button" style={{ marginLeft: 4 }} onClick={() => setPharmaFor(u)}>{u.pharmacist ? 'Editar' : 'Marcar'}</button>}
                    </td>
                    <td className="small">{u.pending ? <Tag tone="warn">convite pendente</Tag> : u.lastLoginAt ? dateTime(u.lastLoginAt) : '—'}</td>
                    <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {canManage && u.pending && <button className="btn sm quiet" type="button" disabled={resend.isPending} onClick={() => resend.mutate(u)}><Mail size={14} /> Reenviar convite</button>}
                      {canManage && !u.me && (
                        <button className="btn sm quiet" type="button" disabled={remove.isPending} aria-label={`Remover ${u.name}`}
                          onClick={() => confirm(`Remover o acesso de ${u.name} à ${me.tenant.name}?`) && remove.mutate(u.id)}>
                          <Trash2 size={14} /> Remover
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editing && <EditUser u={editing} groups={groups} onClose={() => setEditing(null)} onDone={() => { setEditing(null); refresh(); }} />}
      {pharmaFor && (
        <PharmacistModal u={pharmaFor} saving={setPharma.isPending} error={setPharma.error}
          onClose={() => { setPharmaFor(null); setPharma.reset(); }}
          onSave={(v) => setPharma.mutate({ id: pharmaFor.id, ...v }, { onSuccess: () => setPharmaFor(null) })} />
      )}
      {inviting && <Invite groups={groups} onClose={() => setInviting(false)} onDone={(m) => { setInviting(false); setNotice(m); refresh(); }} />}
    </>
  );
}

function EditUser({ u, groups, onClose, onDone }: { u: UserRow; groups: Group[]; onClose: () => void; onDone: () => void }) {
  const [owner, setOwner] = useState(u.owner);
  const [sel, setSel] = useState(u.groups);
  const save = useMutation({ mutationFn: () => api(`/account/users/${u.id}`, { method: 'PUT', json: { owner, groups: sel } }), onSuccess: onDone });
  return (
    <Modal title={`Acesso: ${u.name}`} onClose={onClose}>
      <div style={{ display: 'grid', gap: 12 }}>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" className="check" checked={owner} disabled={u.me} onChange={(e) => setOwner(e.target.checked)} />
          <span><b>Dono</b> — acessa tudo e gerencia usuários e permissões</span>
        </label>
        {!owner && <><div className="small muted">Grupos (as permissões somam):</div><GroupPicker value={sel} onChange={setSel} groups={groups} /></>}
        {save.error && <ErrorBox error={save.error} />}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" type="button" onClick={onClose}>Cancelar</button>
          <button className="btn primary" type="button" disabled={save.isPending || (!owner && !sel.length)} onClick={() => save.mutate()}>{save.isPending ? 'Salvando…' : 'Salvar'}</button>
        </div>
      </div>
    </Modal>
  );
}

function Invite({ groups, onClose, onDone }: { groups: Group[]; onClose: () => void; onDone: (msg: string) => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [sel, setSel] = useState<string[]>(['sac']);
  const [pharmacist, setPharmacist] = useState(false);
  const [crf, setCrf] = useState('');
  const invite = useMutation({
    mutationFn: () => api<{ existing: boolean; mailSent: boolean; link?: string }>('/account/users', { method: 'POST', json: { name, email, groups: sel, pharmacist, ...(pharmacist ? { crf } : {}) } }),
    onSuccess: (r) => onDone(
      r.existing ? `${email} já tinha conta na Berytus: o acesso foi liberado e ele(a) foi avisado(a) por e-mail.`
        : r.mailSent ? `Convite enviado para ${email}. O link para criar a senha vale 7 dias.`
        : `Usuário criado, mas o e-mail não saiu. Mande este link para ${name}: ${r.link}`,
    ),
  });
  const submit = (e: FormEvent) => { e.preventDefault(); invite.mutate(); };
  return (
    <Modal title="Convidar usuário" onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
        <div className="field"><label htmlFor="inv-name">Nome</label><input id="inv-name" className="input" required minLength={2} value={name} onChange={(e) => setName(e.target.value)} autoFocus /></div>
        <div className="field"><label htmlFor="inv-email">E-mail</label><input id="inv-email" className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></div>
        <div className="field"><label>Grupos</label><GroupPicker value={sel} onChange={setSel} groups={groups} /></div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" className="check" checked={pharmacist} onChange={(e) => setPharmacist(e.target.checked)} />
          <span>Farmacêutico com CRF — aprova ou recusa receitas de medicamentos</span>
        </label>
        {pharmacist && (
          <div className="field">
            <label htmlFor="inv-crf">CRF</label>
            <input id="inv-crf" className="input" required placeholder="SP-12345" value={crf} onChange={(e) => setCrf(e.target.value)} />
            <span className="small muted">UF e número do Conselho Regional de Farmácia. Fica registrado em cada receita aceita ou recusada.</span>
          </div>
        )}
        {invite.error && <ErrorBox error={invite.error} />}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" type="button" onClick={onClose}>Cancelar</button>
          <button className="btn primary" type="submit" disabled={invite.isPending || !sel.length}>{invite.isPending ? 'Enviando…' : 'Enviar convite'}</button>
        </div>
      </form>
    </Modal>
  );
}

const LEVEL_LABEL: Record<'' | Level, string> = { '': '—', read: 'Ver', write: 'Alterar' };

/** Tabela áreas × grupos. Cada coluna (grupo) salva separado; Super admin é sempre "tudo". */
function GroupsTab({ groups, canManage }: { groups: Group[]; canManage: boolean }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Record<string, Perms>>({});
  const [creating, setCreating] = useState(false);
  const dirty = (k: string) => draft[k] !== undefined;
  const permsOf = (g: Group) => draft[g.key] ?? g.perms;
  const refresh = () => { setDraft({}); void qc.invalidateQueries({ queryKey: ['access-groups'] }); };
  const save = useMutation({
    mutationFn: async () => { for (const [key, perms] of Object.entries(draft)) await api(`/account/groups/${key}`, { method: 'PUT', json: { perms } }); },
    onSuccess: refresh,
  });
  const reset = useMutation({ mutationFn: (key: string) => api(`/account/groups/${key}/reset`, { method: 'POST', json: {} }), onSuccess: refresh });
  const del = useMutation({ mutationFn: (key: string) => api(`/account/groups/${key}`, { method: 'DELETE' }), onSuccess: refresh });
  const sections = useMemo(() => [...new Set(PERMISSIONS.map((p) => p.section))], []);
  const set = (g: Group, key: PermKey, v: '' | Level) => {
    const next = { ...permsOf(g) };
    if (v) next[key] = v; else delete next[key];
    setDraft({ ...draft, [g.key]: next });
  };
  const changed = Object.keys(draft).length;

  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        <span className="small muted">— nada · Ver: abre a tela e consulta · Alterar: também cria, muda e apaga. Aprovar receita exige CRF, em qualquer grupo.</span>
        <span style={{ flex: 1 }} />
        {canManage && <button className="btn" type="button" onClick={() => setCreating(true)}><Plus size={14} /> Novo grupo</button>}
        {canManage && <button className="btn primary" type="button" disabled={!changed || save.isPending} onClick={() => save.mutate()}><Save size={14} /> {save.isPending ? 'Salvando…' : changed ? `Salvar (${changed} grupo${changed > 1 ? 's' : ''})` : 'Salvar'}</button>}
      </div>
      {(save.error ?? reset.error ?? del.error) && <ErrorBox error={save.error ?? reset.error ?? del.error} />}
      <div className="panel flush" style={{ overflowX: 'auto' }}>
        <table className="simple">
          <thead>
            <tr>
              <th style={{ minWidth: 240 }}>Área</th>
              {groups.map((g) => (
                <th key={g.key} style={{ textAlign: 'center', minWidth: 104, background: dirty(g.key) ? 'color-mix(in srgb, var(--warn) 12%, transparent)' : undefined }}>
                  <div>{g.name}</div>
                  <div className="small muted" style={{ fontWeight: 400 }}><UsersIcon size={11} /> {g.users}</div>
                  {canManage && g.system && g.customized && !g.full && <button className="btn sm quiet" type="button" title="Voltar às permissões padrão" onClick={() => confirm(`Voltar o grupo ${g.name} às permissões padrão?`) && reset.mutate(g.key)}><RotateCcw size={12} /> padrão</button>}
                  {canManage && !g.system && <button className="btn sm quiet" type="button" title="Apagar grupo" onClick={() => confirm(`Apagar o grupo ${g.name}? ${g.users} usuário(s) saem dele.`) && del.mutate(g.key)}><Trash2 size={12} /></button>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sections.map((sec) => (
              <FragmentRows key={sec} title={sec} span={groups.length + 1}>
                {PERMISSIONS.filter((p) => p.section === sec).map((p) => (
                  <tr key={p.key}>
                    <td><div>{p.label}</div>{p.write && <div className="small muted">Alterar: {p.write}</div>}</td>
                    {groups.map((g) => {
                      const v = (permsOf(g)[p.key as PermKey] ?? '') as '' | Level;
                      if (g.full) return <td key={g.key} style={{ textAlign: 'center' }}><Tag tone="info">tudo</Tag></td>;
                      return (
                        <td key={g.key} style={{ textAlign: 'center' }}>
                          {canManage ? (
                            <select className="select" style={{ height: 30, width: 'auto', fontWeight: v ? 600 : 400, color: v === 'write' ? 'var(--ok)' : v === 'read' ? 'var(--info, inherit)' : 'var(--muted)' }}
                              aria-label={`${p.label} — ${g.name}`} value={v} onChange={(e) => set(g, p.key as PermKey, e.target.value as '' | Level)}>
                              <option value="">{LEVEL_LABEL['']}</option>
                              <option value="read">{LEVEL_LABEL.read}</option>
                              {p.write && <option value="write">{LEVEL_LABEL.write}</option>}
                            </select>
                          ) : <span className={v ? '' : 'muted'}>{LEVEL_LABEL[v]}</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </FragmentRows>
            ))}
            <tr>
              <td><div>Usuários e permissões</div><div className="small muted">Esta tela</div></td>
              {groups.map((g) => <td key={g.key} style={{ textAlign: 'center' }} className="small muted">só o dono</td>)}
            </tr>
          </tbody>
        </table>
      </div>
      {creating && <NewGroup groups={groups} onClose={() => setCreating(false)} onDone={() => { setCreating(false); refresh(); }} />}
    </>
  );
}

function FragmentRows({ title, span, children }: { title: string; span: number; children: React.ReactNode }) {
  return (
    <>
      <tr><td colSpan={span} style={{ background: 'var(--surface-2)', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.04em' }}>{title}</td></tr>
      {children}
    </>
  );
}

function NewGroup({ groups, onClose, onDone }: { groups: Group[]; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [from, setFrom] = useState('');
  const create = useMutation({
    mutationFn: () => api('/account/groups', { method: 'POST', json: { name, perms: groups.find((g) => g.key === from)?.perms ?? {} } }),
    onSuccess: onDone,
  });
  return (
    <Modal title="Novo grupo" onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); create.mutate(); }} style={{ display: 'grid', gap: 12 }}>
        <div className="field"><label htmlFor="g-name">Nome</label><input id="g-name" className="input" required minLength={2} maxLength={60} value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="ex.: Compras" /></div>
        <div className="field">
          <label htmlFor="g-from">Começar com as permissões de</label>
          <select id="g-from" className="select" value={from} onChange={(e) => setFrom(e.target.value)}>
            <option value="">nenhuma (tudo desligado)</option>
            {groups.filter((g) => !g.full).map((g) => <option key={g.key} value={g.key}>{g.name}</option>)}
          </select>
          <span className="small muted">Depois ajuste na tabela.</span>
        </div>
        {create.error && <ErrorBox error={create.error} />}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" type="button" onClick={onClose}>Cancelar</button>
          <button className="btn primary" type="submit" disabled={create.isPending}>{create.isPending ? 'Criando…' : 'Criar grupo'}</button>
        </div>
      </form>
    </Modal>
  );
}

/** Marcar/desmarcar farmacêutico e o CRF. */
function PharmacistModal({ u, saving, error, onClose, onSave }: {
  u: UserRow; saving: boolean; error: unknown; onClose: () => void; onSave: (v: { pharmacist: boolean; crf?: string | null }) => void;
}) {
  const [crf, setCrf] = useState(u.crf ?? '');
  const submit = (e: FormEvent) => { e.preventDefault(); onSave({ pharmacist: true, crf }); };
  return (
    <Modal title={`Farmacêutico: ${u.name}`} onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
        <p className="small" style={{ margin: 0 }}>O farmacêutico aceita ou recusa as receitas dos pedidos de medicamentos do Mercado Livre. O CRF fica registrado em cada decisão.</p>
        <div className="field">
          <label htmlFor="ph-crf">CRF</label>
          <input id="ph-crf" className="input" required placeholder="SP-12345" value={crf} onChange={(e) => setCrf(e.target.value)} autoFocus />
          <span className="small muted">UF e número, ex.: SP-12345.</span>
        </div>
        {error ? <ErrorBox error={error} /> : null}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', flexWrap: 'wrap' }}>
          {u.pharmacist
            ? <button className="btn danger" type="button" disabled={saving} onClick={() => onSave({ pharmacist: false })}>Deixar de ser farmacêutico</button>
            : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" type="button" onClick={onClose}>Cancelar</button>
            <button className="btn primary" type="submit" disabled={saving || !crf.trim()}>{saving ? 'Salvando…' : 'Salvar'}</button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
